/**
 * The locate resolver: what a link's focus is, live, and which floor it
 * lands on (twin slice 1, plan W4 step 6; catalogue §8).
 *
 * ONE RESOLVER FOR BOTH VIEWS. The Map and the Building view land on the
 * same floor and ring the same thing because they read the same answer
 * from here. It is pure: the Overview (W4 step 10) feeds it the live
 * lists, the presence map and the record lookups, and re-asks whenever
 * any of them moves; the camera moves only on the landing, never on a
 * later answer (that is the Overview's rule, not this module's).
 *
 * WHAT EACH FOCUS LANDS ON
 *
 *   robot  Where presence (W3) draws it. Inside a car -- `cabinClearanceM
 *          === 0`, robot-presence's own `isInsideCar`, never the 0.225 m
 *          release clearance `heldLift` uses -- it is the CAR's reported
 *          floor, rung on the car, with "Inside SL1". A robot boarding or
 *          waiting at the hall is on its own floor. Lost: its ghost's floor
 *          ("Not reporting. Last seen 14:02."). Lost with its ghost
 *          standing inside a car (presence's `liftName`: a silent rider):
 *          named in the CAR, rung on the car, and NO floor -- the whole
 *          building ("Not reporting. Last seen in SL1 at 14:52.", the
 *          drawer's and Right now's "last seen in SL1"). Its pose names
 *          the floor it boarded on, and where the car is now is not where
 *          a robot nobody hears is. Not placeable: presence's "?" row
 *          floor ("Lost its place on the map.").
 *   lift   `liftLandingLevel`: the floor of the robot that owns or waits
 *          on the car, else the car's floor, else the whole building.
 *   door   Its floor from the REGISTRY (the live door record has none).
 *   zone   Its place's floor. A place the current map does not have (map
 *          0.13.0 removed 14, every L06-L17 `*-GLL` among them) is said
 *          out loud -- "L09-GLL isn't on the current map; it may have
 *          been renamed." -- and lands on the floor its name gives, if any.
 *   level  That floor, in the site's own spelling.
 *   task   Active (or interrupted) with a robot: that robot. Queued: its
 *          zone. Ended: its zone, with "Finished 14:10 (failed)". A purged
 *          record: "That job is no longer on record." A simulated fleet's
 *          job is flagged for the test-robot card -- only when every
 *          robot its name could mean is a test robot.
 *   alert  Read back from its row (closed rows too), then its
 *          `sourceKind`/`sourceId`: a bare robot id is fleet-qualified
 *          from the roster. A closed alert adds "Cleared at 10:55.
 *          Showing SL1 as it is now."; a row about something not on the
 *          map (a gateway, a whole site) lands on the whole building and
 *          says so.
 *
 * A lift or door the active renderer does not draw (the `inventory`
 * input) still lands on its floor, with "Not drawn in this building
 * view." (or "... on this map." in 2D) and reason `not-modelled`.
 *
 * WHILE DATA LOADS. Until the record and everything its floor depends on
 * have arrived, the answer is "Finding X…". From `FIND_TIMEOUT_MS` (5 s)
 * after the landing began it is "Couldn't find X in the live data.", and
 * whatever has arrived by then is used: a lift whose session robot's
 * roster never came lands on the car's own floor, rather than waiting
 * for ever. The link's `level` is only a hint: it is offered while
 * finding and when nothing better is known, never over a live answer.
 *
 * Nothing here says "Nothing is wrong with SL1 now" (catalogue §8, an
 * object healthy now): that needs the attention list, and is the
 * callout's join (W4 step 10 with W5), not the resolver's.
 *
 * Every line of words here is copy for review (plan gate B).
 */
import type { RobotStateView, TaskStateView } from '../api/rmf-client';
import { robotReceiptKey } from '../api/robot-receipts';
import {
  SETTLING_TEXT,
  UNHEARD_CAUSES,
  isInsideCar,
  type PresenceCause,
  type PresenceKind,
  type RobotPresence,
} from '../screens/Map/robot-presence';
import { canonicalLevel, sameLevel } from '../utils/levels';
import { dayClockText, levelWords, type SiteTimeZone } from './attention/words';
import { liftLandingLevel, sessionRobotIds, type LandingLift } from './lift-landing';
import type { LocateFocus } from './locate';
import { isSimulatedFleet } from './simulated-fleet';

/** How long "Finding X…" may last before it becomes "Couldn't find X". */
export const FIND_TIMEOUT_MS = 5_000;

// ---------------------------------------------------------------------------
// Inputs
// ---------------------------------------------------------------------------

/**
 * The alert fields the resolver reads: a structural subset, so it takes
 * today's `AlertViewModel` and the fields W5C adds (`sourceKind`,
 * `sourceId`) without depending on either's shape.
 */
export interface LocateAlert {
  readonly id: string;
  readonly isOpen: boolean;
  readonly sourceKind?: string | null;
  readonly sourceId?: string | null;
  readonly resolvedAtMs?: number | null;
}

/** The task fields the resolver reads. */
export interface LocateTask {
  readonly id: string;
  readonly status: TaskStateView['status'];
  /** The robot's BARE name, as tasks carry it. */
  readonly assignedRobot?: string | null;
  /**
   * The place it cleans: from its phases, or from the dispatch request
   * (`useTaskRequest`) when its phases are empty -- the caller's join.
   */
  readonly zone?: string | null;
  /** Epoch ms it reached a terminal state. */
  readonly completedAtMs?: number | null;
}

/**
 * One record read by id. `gone` is the server saying there is no such
 * record (a 404: purged); `forbidden` is a 403; `error` is not having
 * been able to ask, which reads like loading until the time is up.
 */
export type RecordLookup<T> =
  | { readonly state: 'loading' }
  | { readonly state: 'found'; readonly record: T }
  | { readonly state: 'gone' }
  | { readonly state: 'forbidden' }
  | { readonly state: 'error' };

/** What the active renderer draws, by name (W4 step 12 supplies it). */
export interface LocateInventory {
  readonly view: '2d' | '3d';
  readonly lifts: ReadonlySet<string>;
  readonly doors: ReadonlySet<string>;
}

export type LocateRobot = Pick<RobotStateView, 'name' | 'fleet' | 'location'>;

export interface LocateInputs {
  readonly focus: LocateFocus;
  /** The link's `level`, as written. Only a hint. */
  readonly levelHint: string | null;
  /** Monotonic ms since the landing began. */
  readonly elapsedMs: number;
  /** Each list is undefined until it has loaded. */
  readonly robots: readonly LocateRobot[] | undefined;
  /** `useRobotPresence`, keyed `<fleet>/<robot>`. */
  readonly presence: ReadonlyMap<string, RobotPresence> | undefined;
  readonly lifts: readonly LandingLift[] | undefined;
  /** The door REGISTRY: the live door record carries no level. */
  readonly doors: readonly { readonly name: string; readonly level: string }[] | undefined;
  /** The place registry (`usePlaces`). */
  readonly places: readonly { readonly name: string; readonly level: string }[] | undefined;
  /** The site's floors (`useBuildingMapLevels`). */
  readonly levels: readonly string[] | undefined;
  readonly alert: (alertId: string) => RecordLookup<LocateAlert>;
  readonly task: (taskId: string) => RecordLookup<LocateTask>;
  /** Null when no renderer has said what it draws. */
  readonly inventory: LocateInventory | null;
  readonly clock: SiteTimeZone;
  /**
   * This device's wall clock (`Date.now()`), read only to name the day of
   * a time that is not today ("Last seen yesterday 23:58"), as the
   * Overview heading and the verdict do; never subtracted from a stamp.
   */
  readonly wallNowMs: number;
}

// ---------------------------------------------------------------------------
// Output
// ---------------------------------------------------------------------------

/**
 * Why the ring is not a plain ring on a live object. `other-floor` (W4
 * step 12) is the renderer's: it depends on the floor in view.
 *
 *   not-reporting  a lost robot: its ghost, or nothing to ring
 *   not-placed     a robot reporting but not placeable ("?")
 *   not-modelled   a lift or door this renderer does not draw
 *   in-lift        a robot inside a car: the CAR is rung
 *
 * A lost robot last seen inside a car is `not-reporting` with the CAR
 * rung (its ring is a lift) and no floor.
 */
export type LocateReason = 'not-reporting' | 'not-placed' | 'not-modelled' | 'in-lift';

export type LocateTarget =
  | { readonly kind: 'robot'; readonly fleetName: string; readonly robotName: string }
  | { readonly kind: 'lift'; readonly liftName: string }
  | { readonly kind: 'door'; readonly doorName: string }
  | { readonly kind: 'zone'; readonly place: string }
  | { readonly kind: 'level'; readonly levelName: string };

export interface LocateFound {
  readonly status: 'found';
  /** The floor to land on; null for the whole building. */
  readonly level: string | null;
  /** What the link names, found live; null when there is nothing to ring. */
  readonly target: LocateTarget | null;
  /** What the ring goes on: the car, for a robot inside one. */
  readonly ring: LocateTarget | null;
  readonly reason: LocateReason | null;
  /** Callout lines, in order. */
  readonly notes: readonly string[];
  /** A simulated fleet's robot or job: the test-robot card. */
  readonly simulated: boolean;
  /** The robot's presence, for a robot target. */
  readonly presence: PresenceKind | null;
}

export type LocateResolution =
  | {
      readonly status: 'finding';
      /** PROVISIONAL: the link's hint, in the site's spelling. */
      readonly level: string | null;
      readonly text: string;
      /** When this becomes "Couldn't find". */
      readonly msUntilChange: number;
    }
  | { readonly status: 'not-found'; readonly level: string | null; readonly text: string }
  | LocateFound;

// ---------------------------------------------------------------------------
// Words
// ---------------------------------------------------------------------------

/** How a focus is named in "Finding X…". */
export function focusName(focus: LocateFocus): string {
  switch (focus.kind) {
    case 'robot':
      return focus.robotName;
    case 'lift':
      return focus.liftName;
    case 'door':
      return focus.doorName;
    case 'zone':
      return focus.place;
    case 'level':
      return levelWords(focus.levelName) ?? focus.levelName;
    case 'task':
      return 'that job';
    case 'alert':
      return 'that alert';
  }
}

export const findingText = (name: string) => `Finding ${name}…`;
export const notFoundText = (name: string) => `Couldn't find ${name} in the live data.`;
export const notDrawnText = (view: LocateInventory['view']) =>
  view === '3d' ? 'Not drawn in this building view.' : 'Not drawn on this map.';
export const renamedText = (place: string) =>
  `${place} isn't on the current map; it may have been renamed.`;
export const insideText = (lift: string) => `Inside ${lift}.`;
export const GONE_TEXT = {
  task: 'That job is no longer on record.',
  alert: 'That alert is no longer on record.',
} as const;
export const FORBIDDEN_TEXT = {
  task: "You don't have access to that job.",
  alert: "You don't have access to that alert.",
} as const;
export const NOT_ON_MAP_ALERT_TEXT = "This alert isn't about one place in the building.";
export const NO_PLACE_TASK_TEXT = 'That job has no place on record.';
export const LOST_PLACE_TEXT = 'Lost its place on the map.';

function ambiguousText(robot: string): string {
  return `More than one robot is called ${robot}; this link doesn't say which.`;
}

/** The site's clock, with the day named when it is not today. */
type When = Pick<LocateInputs, 'clock' | 'wallNowMs'>;
const timeText = (ms: number, w: When): string => dayClockText(ms, w.clock, w.wallNowMs);

function lostText(atMs: number | null, w: When): string {
  return atMs !== null && Number.isFinite(atMs)
    ? `Not reporting. Last seen ${timeText(atMs, w)}.`
    : 'Not reporting, and no position on record.';
}

/**
 * A lost robot whose ghost stands inside `lift`: the car, never a floor
 * -- the words the drawer (`robot-lines`) and Right now use. "Not
 * reporting" only where the robot itself is unheard (`UNHEARD_CAUSES`);
 * the (0, 0) sentinel, which a robot still reporting can send, is "Last
 * recorded in SL1", as the drawer says it.
 */
export function lostInCarText(
  lift: string,
  cause: PresenceCause,
  atMs: number | null,
  w: When,
): string {
  const at = atMs !== null && Number.isFinite(atMs) ? ` at ${timeText(atMs, w)}` : '';
  return UNHEARD_CAUSES.has(cause)
    ? `Not reporting. Last seen in ${lift}${at}.`
    : `Last recorded in ${lift}${at}.`;
}

function finishedText(task: LocateTask, w: When): string {
  const at = task.completedAtMs;
  const when = typeof at === 'number' && Number.isFinite(at) ? ` ${timeText(at, w)}` : '';
  const how = task.status === 'completed' ? '' : ` (${task.status})`;
  return `Finished${when}${how}.`;
}

function clearedText(alert: LocateAlert, subject: string, w: When): string {
  const at = alert.resolvedAtMs;
  const when = typeof at === 'number' && Number.isFinite(at) ? ` at ${timeText(at, w)}` : '';
  return `Cleared${when}. Showing ${subject} as it is now.`;
}

// ---------------------------------------------------------------------------
// Resolution
// ---------------------------------------------------------------------------

/** Not answerable yet: names the thing still being looked for. */
interface Missing {
  readonly status: 'missing';
  readonly name: string;
}

type Step = LocateFound | Missing;

interface Ctx extends LocateInputs {
  /** Still inside the 5 s: worth waiting for data that could change the answer. */
  readonly patient: boolean;
  /** The hint in the site's spelling, or null. */
  readonly hint: string | null;
}

const missing = (name: string): Missing => ({ status: 'missing', name });

/** `level` in the site's own spelling when the site has a floor by that
 *  name in either spelling; as given otherwise. */
function siteLevel(level: string, levels: readonly string[] | undefined): string {
  return levels?.find((l) => sameLevel(l, level)) ?? level;
}

/** The hint, in the site's spelling; null when the site has no such floor. */
function hintLevel(hint: string | null, levels: readonly string[] | undefined): string | null {
  if (!hint) return null;
  if (!levels?.length) return canonicalLevel(hint);
  return levels.find((l) => sameLevel(l, hint)) ?? null;
}

function found(
  ctx: Ctx,
  over: Partial<Omit<LocateFound, 'status'>> & { level: string | null },
): LocateFound {
  return {
    status: 'found',
    target: null,
    ring: null,
    reason: null,
    notes: [],
    simulated: false,
    presence: null,
    ...over,
    level: over.level === null ? null : siteLevel(over.level, ctx.levels),
  };
}

function withNotes(step: Step, before: readonly string[], after: readonly string[] = []): Step {
  if (step.status !== 'found') return step;
  return { ...step, notes: [...before, ...step.notes, ...after] };
}

function resolveRobot(ctx: Ctx, fleetName: string, robotName: string): Step {
  if (!ctx.robots) return missing(robotName);
  const robot = ctx.robots.find((r) => r.fleet === fleetName && r.name === robotName);
  if (!robot) return missing(robotName);
  // Whether it is inside a car needs the cars.
  if (!ctx.lifts && ctx.patient) return missing(robotName);
  const target: LocateTarget = { kind: 'robot', fleetName, robotName };
  const simulated = isSimulatedFleet(fleetName);
  const p = ctx.presence?.get(robotReceiptKey(fleetName, robotName));
  const kind = p?.kind ?? null;
  const base = { target, ring: target, simulated, presence: kind };

  if (kind === 'riding') {
    const car = ctx.lifts?.find((l) => l.name === p!.liftName);
    const liftName = p!.liftName ?? '';
    return found(ctx, {
      ...base,
      level: car?.currentLevel || null,
      ring: { kind: 'lift', liftName },
      reason: 'in-lift',
      notes: [insideText(liftName)],
    });
  }
  if (kind === 'lost' && p!.liftName) {
    // A silent rider's ghost: its pose names the floor it BOARDED on, and
    // the car may be anywhere since. Rung on the car, named in it, and
    // landed on the whole building -- never the boarding floor, and never
    // the link's hint, which a producer may have taken from that pose.
    return found(ctx, {
      ...base,
      level: null,
      ring: { kind: 'lift', liftName: p!.liftName },
      reason: 'not-reporting',
      notes: [lostInCarText(p!.liftName, p!.cause, p!.pose?.atMs ?? null, ctx)],
    });
  }
  if (kind === 'lost') {
    return found(ctx, {
      ...base,
      level: p!.pose?.map || ctx.hint,
      reason: 'not-reporting',
      notes: [lostText(p!.pose?.atMs ?? null, ctx)],
    });
  }
  if (kind === 'unknown') {
    return found(ctx, {
      ...base,
      level: p!.level || ctx.hint,
      reason: 'not-placed',
      notes: [LOST_PLACE_TEXT],
    });
  }

  // Reporting (live, quiet, checking, settling), or not yet classified.
  const loc = robot.location;
  const pose = p?.pose ?? (loc && loc.map && !(loc.x === 0 && loc.y === 0) ? loc : null);
  if (!pose) {
    return found(ctx, {
      ...base,
      level: ctx.hint,
      reason: 'not-reporting',
      notes: [lostText(null, ctx)],
    });
  }
  const settling = kind === 'settling' ? [SETTLING_TEXT] : [];
  const car = (ctx.lifts ?? []).find((l) => isInsideCar(l, pose));
  if (car) {
    return found(ctx, {
      ...base,
      level: car.currentLevel || pose.map,
      ring: { kind: 'lift', liftName: car.name },
      reason: 'in-lift',
      notes: [insideText(car.name), ...settling],
    });
  }
  return found(ctx, { ...base, level: pose.map, notes: settling });
}

/** `<fleet>/<robot>` split in two; null for a bare name, or an id with
 *  an empty half (looked for as written). */
function qualifiedId(id: string): { fleet: string; name: string } | null {
  const slash = id.indexOf('/');
  if (slash <= 0 || slash === id.length - 1) return null;
  return { fleet: id.slice(0, slash), name: id.slice(slash + 1) };
}

/** A robot named by its bare name (tasks, today's robot alerts). */
function resolveBareRobot(ctx: Ctx, robotName: string): Step {
  const qualified = qualifiedId(robotName);
  if (qualified) return resolveRobot(ctx, qualified.fleet, qualified.name);
  if (!ctx.robots) return missing(robotName);
  const matches = ctx.robots.filter((r) => r.name === robotName);
  if (matches.length === 1) return resolveRobot(ctx, matches[0]!.fleet, robotName);
  if (matches.length === 0) return missing(robotName);
  return found(ctx, { level: ctx.hint, notes: [ambiguousText(robotName)] });
}

function notDrawn(ctx: Ctx, kind: 'lifts' | 'doors', name: string) {
  const inv = ctx.inventory;
  if (!inv || inv[kind].has(name)) return {};
  return { reason: 'not-modelled' as const, notes: [notDrawnText(inv.view)] };
}

function resolveLift(ctx: Ctx, liftName: string): Step {
  if (!ctx.lifts) return missing(liftName);
  const lift = ctx.lifts.find((l) => l.name === liftName);
  if (!lift) return missing(liftName);
  // Its floor may be its session robot's: worth waiting for the roster.
  if (!ctx.robots && ctx.patient && sessionRobotIds(lift).length > 0) return missing(liftName);
  const landing = liftLandingLevel(lift, ctx.robots, ctx.presence);
  const target: LocateTarget = { kind: 'lift', liftName };
  return found(ctx, {
    level: landing.level,
    target,
    ring: target,
    ...notDrawn(ctx, 'lifts', liftName),
  });
}

function resolveDoor(ctx: Ctx, doorName: string): Step {
  if (!ctx.doors) return missing(doorName);
  const door = ctx.doors.find((d) => d.name === doorName);
  if (!door) return missing(doorName);
  const target: LocateTarget = { kind: 'door', doorName };
  return found(ctx, {
    level: door.level || null,
    target,
    ring: target,
    ...notDrawn(ctx, 'doors', doorName),
  });
}

/** The floor a place's own name gives (`L09-GLL` -> L9), if the site has it. */
function levelFromPlaceName(place: string, levels: readonly string[] | undefined): string | null {
  const m = /^(L\d+M?)(?:[-_\s]|$)/i.exec(place);
  if (!m) return null;
  const level = canonicalLevel(m[1]!.toUpperCase());
  if (!levels?.length) return level;
  return levels.find((l) => sameLevel(l, level)) ?? null;
}

function resolveZone(ctx: Ctx, place: string): Step {
  // An EMPTY registry is not "every place was removed": it is what a
  // worker restart or the storage wedge answers. It cannot check anything.
  if (!ctx.places?.length) return missing(place);
  const row = ctx.places.find((p) => p.name === place);
  if (row) {
    const target: LocateTarget = { kind: 'zone', place };
    return found(ctx, { level: row.level || null, target, ring: target });
  }
  if (!ctx.levels && ctx.patient && /^L\d/i.test(place)) return missing(place);
  const level = levelFromPlaceName(place, ctx.levels);
  return found(ctx, {
    level,
    ring: level ? { kind: 'level', levelName: level } : null,
    notes: [renamedText(place)],
  });
}

function resolveLevel(ctx: Ctx, levelName: string): Step {
  const name = levelWords(levelName) ?? levelName;
  if (!ctx.levels?.length) return missing(name);
  const level = ctx.levels.find((l) => sameLevel(l, levelName));
  if (!level) return missing(name);
  const target: LocateTarget = { kind: 'level', levelName: level };
  return found(ctx, { level, target, ring: target });
}

function resolveTask(ctx: Ctx, taskId: string): Step {
  const look = ctx.task(taskId);
  switch (look.state) {
    case 'loading':
    case 'error':
      return missing('that job');
    case 'gone':
      return found(ctx, { level: ctx.hint, notes: [GONE_TEXT.task] });
    case 'forbidden':
      return found(ctx, { level: ctx.hint, notes: [FORBIDDEN_TEXT.task] });
    case 'found':
      break;
  }
  const task = look.record;
  const assigned = task.assignedRobot || null;
  // A test robot's job only when every robot the job could mean is one:
  // a name two fleets share does not say which, and a real robot's job
  // must never wear the test-robot card.
  const qualified = assigned ? qualifiedId(assigned) : null;
  const fleets = qualified
    ? [qualified.fleet]
    : assigned
      ? (ctx.robots ?? []).filter((r) => r.name === assigned).map((r) => r.fleet)
      : [];
  const simulated = fleets.length > 0 && fleets.every(isSimulatedFleet);
  const mark = (step: Step): Step =>
    step.status === 'found' && simulated ? { ...step, simulated } : step;

  const running = task.status === 'active' || task.status === 'interrupted';
  if (running && assigned) {
    const step = resolveBareRobot(ctx, assigned);
    // A running job's robot that the roster cannot name falls back to the
    // job's place, once there is no more point waiting.
    if (step.status === 'found' || ctx.patient || !task.zone) return mark(step);
  }
  const ended =
    task.status === 'completed' || task.status === 'failed' || task.status === 'cancelled';
  const after = ended ? [finishedText(task, ctx)] : [];
  if (!task.zone) {
    return mark(found(ctx, { level: ctx.hint, notes: [NO_PLACE_TASK_TEXT, ...after] }));
  }
  return mark(withNotes(resolveZone(ctx, task.zone), [], after));
}

/** What a row's source names in "Showing X as it is now". */
function sourceName(kind: string, id: string): string {
  if (kind === 'task') return 'that job';
  const slash = id.indexOf('/');
  return kind === 'robot' && slash > 0 ? id.slice(slash + 1) : id;
}

function resolveAlert(ctx: Ctx, alertId: string): Step {
  const look = ctx.alert(alertId);
  switch (look.state) {
    case 'loading':
    case 'error':
      return missing('that alert');
    case 'gone':
      return found(ctx, { level: ctx.hint, notes: [GONE_TEXT.alert] });
    case 'forbidden':
      return found(ctx, { level: ctx.hint, notes: [FORBIDDEN_TEXT.alert] });
    case 'found':
      break;
  }
  const alert = look.record;
  const kind = alert.sourceKind ?? '';
  const id = alert.sourceId ?? '';
  let step: Step;
  if (id && kind === 'robot') step = resolveBareRobot(ctx, id);
  else if (id && kind === 'lift') step = resolveLift(ctx, id);
  else if (id && kind === 'door') step = resolveDoor(ctx, id);
  else if (id && kind === 'task') step = resolveTask(ctx, id);
  else return found(ctx, { level: ctx.hint, notes: [NOT_ON_MAP_ALERT_TEXT] });
  return alert.isOpen ? step : withNotes(step, [clearedText(alert, sourceName(kind, id), ctx)]);
}

function step(ctx: Ctx): Step {
  const f = ctx.focus;
  switch (f.kind) {
    case 'robot':
      return resolveRobot(ctx, f.fleetName, f.robotName);
    case 'lift':
      return resolveLift(ctx, f.liftName);
    case 'door':
      return resolveDoor(ctx, f.doorName);
    case 'zone':
      return resolveZone(ctx, f.place);
    case 'level':
      return resolveLevel(ctx, f.levelName);
    case 'task':
      return resolveTask(ctx, f.taskId);
    case 'alert':
      return resolveAlert(ctx, f.alertId);
  }
}

/** Pure: what the link's focus is now, and where it lands. */
export function resolveLocate(inputs: LocateInputs): LocateResolution {
  // A non-finite clock is not a reason to say "Finding" for ever.
  const patient = inputs.elapsedMs < FIND_TIMEOUT_MS;
  const hint = hintLevel(inputs.levelHint, inputs.levels);
  const result = step({ ...inputs, patient, hint });
  if (result.status === 'found') return result;
  if (patient) {
    return {
      status: 'finding',
      level: hint,
      text: findingText(result.name),
      msUntilChange: Math.max(0, FIND_TIMEOUT_MS - inputs.elapsedMs),
    };
  }
  return { status: 'not-found', level: hint, text: notFoundText(result.name) };
}
